import { ChartPie, ClipboardCheck } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import {
  getAddCampaignIntegrationPath,
  getCampaignEditPath,
} from "../utils/navigation";

import Icons from "@/components/icons";
import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Card, CardContent } from "@/components/ui/v2/card";
import {
  FiltersDrawer,
  type FilterSectionConfig,
} from "@/components/ui/v2/filters/FiltersDrawer";
import { Skeleton } from "@/components/ui/v2/skeleton";
import {
  TableActiveFilters,
  type ActiveFilter,
} from "@/components/ui/v2/table/table-active-filters";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { useCampaign } from "@/features/campaigns/api/useCampaign";
import { ArchivedSegmentBanner } from "@/features/campaigns/components/ArchivedSegmentBanner";
import { useCampaignJobBoardsInfinite } from "@/features/campaigns/jobboards/api/useCampaignJobBoardsInfinite";
import CampaignJobBoardList from "@/features/campaigns/jobboards/components/CampaignJobBoardList/CampaignJobBoardList";
import CampaignJobBoardListSkeleton from "@/features/campaigns/jobboards/components/CampaignJobBoardList/CampaignJobBoardListSkeleton";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import {
  CAMPAIGN_INTEGRATION_STATUS_CONFIG,
  CAMPAIGN_INTEGRATION_STATUSES,
  type CampaignIntegrationStatus,
} from "@/features/campaigns/jobboards/constants";
import { getCampaignStatus } from "@/features/campaigns/utils/getCampaignStatus";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { clearFilterValues, removeFilterValue } from "@/lib/utils/filterParams";

const FILTER_KEY = "status";

function CampaignDetailPage() {
  const { clientId, campaignId } = useParams();
  const { t } = useTranslation();
  const { canCreate, canEdit } = usePermissionMatrix();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const selectedStatuses = useMemo<CampaignIntegrationStatus[]>(() => {
    const values = searchParams.getAll(
      FILTER_KEY,
    ) as CampaignIntegrationStatus[];
    return values.filter((s) => CAMPAIGN_INTEGRATION_STATUSES.includes(s));
  }, [searchParams]);

  const resolvedClientId = clientId ?? "";
  const resolvedCampaignId = campaignId ?? "";

  const {
    data: campaign,
    isLoading: isCampaignLoading,
    isError: isCampaignError,
  } = useCampaign({
    campaignId: resolvedCampaignId,
    clientId: resolvedClientId,
  });
  const {
    data: jobBoardsData,
    isLoading: isJobBoardsLoading,
    isFetching: isJobBoardsFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCampaignJobBoardsInfinite({
    campaignId: resolvedCampaignId,
    clientId: resolvedClientId,
    statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    search: searchQuery.trim() || undefined,
  });

  const jobBoards = useMemo(() => {
    if (!jobBoardsData?.pages) return [];
    return jobBoardsData.pages.flatMap((page) => page.data);
  }, [jobBoardsData]);

  const campaignStatus = useMemo(() => {
    if (!jobBoardsData?.pages) return null;
    const allJobBoards = jobBoardsData.pages.flatMap((page) => page.data);
    return getCampaignStatus(allJobBoards);
  }, [jobBoardsData]);

  const breadcrumbs = useBreadcrumbs({
    items: [
      {
        label: t("common.campaigns"),
        href: `../`,
      },
      { label: campaign?.name ?? t("common.campaign") },
    ],
  });

  const filterSections: FilterSectionConfig[] = useMemo(
    () => [
      {
        key: FILTER_KEY,
        title: "common.status",
        options: CAMPAIGN_INTEGRATION_STATUSES.map((status) => ({
          label: t(CAMPAIGN_INTEGRATION_STATUS_CONFIG[status].labelKey),
          value: status,
        })),
      },
    ],
    [t],
  );

  const activeFilters: ActiveFilter[] = useMemo(
    () =>
      selectedStatuses.map((status) => ({
        key: FILTER_KEY,
        label: t("common.status"),
        value: status,
        valueLabel: t(CAMPAIGN_INTEGRATION_STATUS_CONFIG[status].labelKey),
      })),
    [selectedStatuses, t],
  );

  const handleRemoveFilter = useCallback(
    (filter: ActiveFilter) => {
      setSearchParams(
        removeFilterValue(searchParams, filter.key, filter.value.toString()),
      );
    },
    [searchParams, setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams(clearFilterValues(searchParams, [FILTER_KEY]));
  }, [searchParams, setSearchParams]);

  if (!campaignId) {
    return null;
  }
  const canCreateJobBoards = canCreate("jobBoards");
  const canEditJobBoards = canEdit("jobBoards");
  const isSegmentArchived = campaign?.segment?.archivedAt != null;
  const addJobBoardLabel = (
    <>
      <Icons.CrossIcon className="h-4 w-4 mr-2 rotate-45" />
      {t("actions.add", { entity: t("common.jobBoard") })}
    </>
  );
  const addJobBoardAction = isSegmentArchived ? (
    <Button size="sm" disabled data-testid="add-job-board">
      {addJobBoardLabel}
    </Button>
  ) : (
    <Button asChild size="sm" data-testid="add-job-board">
      <Link
        to={getAddCampaignIntegrationPath(resolvedClientId, resolvedCampaignId)}
      >
        {addJobBoardLabel}
      </Link>
    </Button>
  );
  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      backbutton={{
        href: "../",
        label: t("common.back"),
      }}
      actions={
        <>
          {canEdit("campaigns") && (
            <Button variant="outline" size="sm" asChild>
              <Link
                to={getCampaignEditPath(resolvedClientId, resolvedCampaignId)}
              >
                <Icons.EditSquareIcon className="h-4 w-4 mr-2" />
                {t("actions.edit", {
                  entity: t("common.campaign"),
                  defaultValue: "Edit Campaign",
                })}
              </Link>
            </Button>
          )}
        </>
      }
      title={
        campaign?.name ? (
          `${campaign?.name} - ${t("common.jobBoards")}`
        ) : (
          <Skeleton className="w-44 h-8" />
        )
      }
      subtitle={
        canCreateJobBoards || canEditJobBoards
          ? t("campaigns.manageJobBoards", {
            defaultValue: "Manage job boards for this campaign",
          })
          : t("campaigns.viewJobBoards", {
            defaultValue: "View and review your campaign's job boards",
          })
      }
      badge={
        campaignStatus ? (
          <Badge variant={campaignStatus.variant} size="medium">
            {t(campaignStatus.labelKey)}
          </Badge>
        ) : undefined
      }
    >
      {isCampaignError || (!isCampaignLoading && !campaign) ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              {t("errors.campaignNotFound")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={<ChartPie className="h-4 w-4 lg:h-5 lg:w-5 text-primary" />}
              label={t("common.segment", { defaultValue: "Segment" })}
              value={
                campaign?.segment?.name ?? <Skeleton className="w-16 h-8" />
              }
              variant="gradient"
              isLoading={isCampaignLoading}
            />
            <MetricCard
              icon={
                <ClipboardCheck className="h-4 w-4 lg:h-5 lg:w-5 text-primary" />
              }
              label={t("common.vacancies", { defaultValue: "Vacancies" })}
              value={campaign?.vacancyCount ?? <Skeleton className="w-8 h-8" />}
              variant="gradient"
              isLoading={isCampaignLoading}
            />
          </div>

          {isSegmentArchived && campaign?.segment && (
            <ArchivedSegmentBanner
              clientId={resolvedClientId}
              campaignId={resolvedCampaignId}
              segmentName={campaign.segment.name}
            />
          )}

          <TableToolbar
            onSearchChange={setSearchQuery}
            searchValue={searchQuery}
            onFiltersClick={() => setIsFiltersOpen(true)}
            actions={canCreateJobBoards ? addJobBoardAction : undefined}
          />

          {activeFilters.length > 0 && (
            <TableActiveFilters
              filters={activeFilters}
              onRemoveFilter={handleRemoveFilter}
              onClearFilters={handleClearFilters}
            />
          )}

          <section className="flex flex-col gap-4">
            {isJobBoardsLoading ||
            (isJobBoardsFetching &&
              !isJobBoardsLoading &&
              jobBoards.length === 0) ? (
                <CampaignJobBoardListSkeleton />
              ) : (
                <CampaignJobBoardList
                  jobBoards={jobBoards}
                  campaignId={resolvedCampaignId}
                  clientId={resolvedClientId}
                  canEdit={canEditJobBoards}
                  canAdd={canCreateJobBoards && !isSegmentArchived}
                  isLoading={isJobBoardsLoading}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                />
              )}
          </section>
        </div>
      )}

      <FiltersDrawer
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        searchParams={searchParams}
        filterKeys={[FILTER_KEY]}
        filterSections={filterSections}
        entityName={t("common.jobBoards")}
        defaultExpanded
      />
    </ModernPageLayout>
  );
}
export default CampaignDetailPage;
